import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Card,
  CardContent,
  TextField,
  Button,
  TablePagination,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  FormControlLabel,
  Switch,
  Collapse,
  Stack,
} from '@mui/material';
import { Edit, Delete, Add, Close, FilterList } from '@mui/icons-material';
import { IconCurrencyDollar } from '@tabler/icons-react';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

interface Customer {
  id: string;
  companyName: string;
  customerType: {
    id: string;
    name: string;
  };
  customerGroup?: { id: string; name: string } | null;
  customerGroupId?: string;
  phoneNumber: string;
  city: string;
  country: {
    id: string;
    name: string;
    isoCode: string;
  };
  postCode: string;
  address: string;
  vatNumber: string;
  evcNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  autotunerEmail?: string | null;
  magicmotorsportsEmail?: string | null;
  slaveSerialNumber?: string | null;
  balance: number;
  isActive: boolean;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

const CustomersPage: React.FC = () => {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [deletingCustomer, setDeletingCustomer] = useState<Customer | null>(
    null
  );
  const [customerTypes, setCustomerTypes] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [customerGroups, setCustomerGroups] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [countries, setCountries] = useState<
    Array<{ id: string; name: string; isoCode: string; phoneCode: string }>
  >([]);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [newCustomer, setNewCustomer] = useState({
    companyName: '',
    customerTypeId: '',
    customerGroupId: '',
    phoneNumber: '',
    city: '',
    countryId: '',
    postCode: '',
    address: '',
    vatNumber: '',
    evcNumber: '',
    email: '',
    autotunerEmail: '',
    magicmotorsportsEmail: '',
    slaveSerialNumber: '',
    password: '',
    firstName: '',
    lastName: '',
    isActive: true,
    isVerified: false,
  });

  const [isAddCreditsModalOpen, setIsAddCreditsModalOpen] =
    useState<boolean>(false);
  const [selectedCustomerForCredits, setSelectedCustomerForCredits] =
    useState<Customer | null>(null);
  const [creditAmount, setCreditAmount] = useState<number>(0);
  const [adjustType, setAdjustType] = useState<'credit' | 'debit'>('credit');
  const [adjustReason, setAdjustReason] = useState<string>('');

  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
  const [filterEmail, setFilterEmail] = useState<string>('');
  const [filterPhone, setFilterPhone] = useState<string>('');
  const [filterFirstName, setFilterFirstName] = useState<string>('');
  const [filterLastName, setFilterLastName] = useState<string>('');
  const [filterCustomerGroupId, setFilterCustomerGroupId] = useState<string>('');
  const [appliedFilterEmail, setAppliedFilterEmail] = useState<string>('');
  const [appliedFilterPhone, setAppliedFilterPhone] = useState<string>('');
  const [appliedFilterFirstName, setAppliedFilterFirstName] = useState<string>('');
  const [appliedFilterLastName, setAppliedFilterLastName] = useState<string>('');
  const [appliedFilterCustomerGroupId, setAppliedFilterCustomerGroupId] = useState<string>('');

  const handleFiltersConfirm = () => {
    setAppliedFilterEmail(filterEmail);
    setAppliedFilterPhone(filterPhone);
    setAppliedFilterFirstName(filterFirstName);
    setAppliedFilterLastName(filterLastName);
    setAppliedFilterCustomerGroupId(filterCustomerGroupId);
    setPage(0);
  };

  const handleFiltersReset = () => {
    setFilterEmail('');
    setFilterPhone('');
    setFilterFirstName('');
    setFilterLastName('');
    setFilterCustomerGroupId('');
    setAppliedFilterEmail('');
    setAppliedFilterPhone('');
    setAppliedFilterFirstName('');
    setAppliedFilterLastName('');
    setAppliedFilterCustomerGroupId('');
    setPage(0);
  };

  // Load customer groups for filter select (and add/edit modals)
  useEffect(() => {
    apiService.getCustomerGroups().then(r => setCustomerGroups(r.groups)).catch(() => {});
  }, []);

  // Load customers from API (depends on applied filters, not raw input)
  useEffect(() => {
    const loadCustomers = async () => {
      try {
        setLoading(true);
        setError(null);

        const customersResponse = await apiService.getCustomers(
          page + 1,
          rowsPerPage,
          {
            email: appliedFilterEmail.trim() || undefined,
            phoneNumber: appliedFilterPhone.trim() || undefined,
            firstName: appliedFilterFirstName.trim() || undefined,
            lastName: appliedFilterLastName.trim() || undefined,
            customerGroupId: appliedFilterCustomerGroupId.trim() || undefined,
          }
        );
        setCustomers(customersResponse.customers);
        setTotal(customersResponse.total);
      } catch (err: any) {
        setError(err.message || t('customers.errors.failedToLoad'));
        console.error('Error loading customers:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCustomers();
  }, [page, rowsPerPage, appliedFilterEmail, appliedFilterPhone, appliedFilterFirstName, appliedFilterLastName, appliedFilterCustomerGroupId]);

  const handleEdit = async (id: string) => {
    try {
      // Load customer types, groups, countries and customer data when opening edit modal
      const [typesResponse, groupsResponse, countriesResponse, customerData] =
        await Promise.all([
          apiService.getCustomerTypes(),
          apiService.getCustomerGroups(),
          apiService.getCountries(),
          apiService.getCustomerById(id),
        ]);

      console.log(customerData);

      setCustomerTypes(typesResponse.customerTypes);
      setCustomerGroups(groupsResponse.groups);
      setCountries(countriesResponse.items);
      setEditingCustomer({
        ...customerData,
        customerGroupId: customerData.customerGroup?.id ?? '',
      });
      setSelectedCountry(customerData.country.id);
      setIsEditModalOpen(true);
    } catch (err) {
      console.error('Failed to load customer data:', err);
      setSnackbar({
        open: true,
        message: t('customers.errors.failedToLoadData'),
        severity: 'error',
      });
    }
  };

  const handleDelete = (id: string) => {
    const customer = customers.find(c => c.id === id);
    if (customer) {
      setDeletingCustomer(customer);
      setIsDeleteModalOpen(true);
    }
  };

  const handleOpenAddCreditsModal = (customer: Customer) => {
    setSelectedCustomerForCredits(customer);
    setCreditAmount(0);
    setIsAddCreditsModalOpen(true);
  };

  const handleCloseAddCreditsModal = () => {
    setIsAddCreditsModalOpen(false);
    setSelectedCustomerForCredits(null);
    setCreditAmount(0);
    setAdjustType('credit');
    setAdjustReason('');
  };

  const handleAddCredits = async () => {
    if (!selectedCustomerForCredits || creditAmount < 1) {
      setSnackbar({
        open: true,
        message: t('customers.messages.invalidCreditAmount'),
        severity: 'error',
      });
      return;
    }

    try {
      await apiService.adjustCustomerBalance(selectedCustomerForCredits.id, {
        type: adjustType,
        amount: creditAmount,
        ...(adjustReason.trim() ? { reason: adjustReason.trim() } : {}),
      });

      setSnackbar({
        open: true,
        message: t('customers.messages.creditsAdded'),
        severity: 'success',
      });

      handleCloseAddCreditsModal();
    } catch (err: any) {
      console.error('Failed to adjust balance:', err);
      setSnackbar({
        open: true,
        message: err.message || t('customers.errors.failedToAddCredits'),
        severity: 'error',
      });
    }
  };

  const handleAddCustomer = async () => {
    // Load customer types, groups and countries when opening modal
    try {
      const [typesResponse, groupsResponse, countriesResponse] =
        await Promise.all([
          apiService.getCustomerTypes(),
          apiService.getCustomerGroups(),
          apiService.getCountries(),
        ]);
      setCustomerTypes(typesResponse.customerTypes);
      setCustomerGroups(groupsResponse.groups);
      setCountries(countriesResponse.items);
    } catch (err) {
      console.error('Failed to load customer types, groups or countries:', err);
    }
    setIsAddModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setSelectedCountry('');
    setNewCustomer({
      companyName: '',
      customerTypeId: '',
      customerGroupId: '',
      phoneNumber: '',
      city: '',
      countryId: '',
      postCode: '',
      address: '',
      vatNumber: '',
      evcNumber: '',
      email: '',
      autotunerEmail: '',
      magicmotorsportsEmail: '',
      slaveSerialNumber: '',
      password: '',
      firstName: '',
      lastName: '',
      isActive: true,
      isVerified: false,
    });
  };

  const handleSaveCustomer = async () => {
    // Determine customer type
    const selectedType = customerTypes.find(
      ct => ct.id === newCustomer.customerTypeId
    );
    const isBusiness = selectedType?.name.toLowerCase().includes('business');
    const isPrivate = selectedType?.name.toLowerCase().includes('private');

    // Validate required fields based on customer type
    const requiredFields: string[] = [
      'email',
      'password',
      'city',
      'phoneNumber',
      'customerTypeId',
    ];

    if (!selectedCountry) {
      requiredFields.push('country');
    }

    if (isBusiness) {
      if (!newCustomer.companyName) {
        requiredFields.push('companyName');
      }
    } else if (isPrivate) {
      if (!newCustomer.firstName) {
        requiredFields.push('firstName');
      }
      if (!newCustomer.lastName) {
        requiredFields.push('lastName');
      }
    }

    // Check if all required fields are filled
    const missingFields: string[] = [];
    if (!newCustomer.email) missingFields.push('email');
    if (!newCustomer.password) missingFields.push('password');
    if (!newCustomer.city) missingFields.push('city');
    if (!newCustomer.phoneNumber) missingFields.push('phoneNumber');
    if (!newCustomer.customerTypeId) missingFields.push('customerType');
    if (!selectedCountry) missingFields.push('country');

    if (isBusiness && !newCustomer.companyName) {
      missingFields.push('companyName');
    } else if (isPrivate) {
      if (!newCustomer.firstName) missingFields.push('firstName');
      if (!newCustomer.lastName) missingFields.push('lastName');
    }

    if (missingFields.length > 0) {
      setSnackbar({
        open: true,
        message: t('customers.messages.fillRequiredFields'),
        severity: 'error',
      });
      return;
    }

    try {
      // Prepare customer data based on type
      const customerData: any = {
        customerTypeId: newCustomer.customerTypeId,
        phoneNumber: newCustomer.phoneNumber,
        city: newCustomer.city,
        countryId: selectedCountry,
        postCode: newCustomer.postCode,
        address: newCustomer.address,
        vatNumber: newCustomer.vatNumber,
        evcNumber: newCustomer.evcNumber,
        email: newCustomer.email,
        autotunerEmail: newCustomer.autotunerEmail,
        magicmotorsportsEmail: newCustomer.magicmotorsportsEmail,
        slaveSerialNumber: newCustomer.slaveSerialNumber || undefined,
        password: newCustomer.password,
        isActive: newCustomer.isActive,
        isVerified: newCustomer.isVerified,
      };
      if (newCustomer.customerGroupId) {
        customerData.customerGroupId = newCustomer.customerGroupId;
      }

      // Add fields based on customer type
      if (isBusiness) {
        customerData.companyName = newCustomer.companyName;
        if (newCustomer.firstName)
          customerData.firstName = newCustomer.firstName;
        if (newCustomer.lastName) customerData.lastName = newCustomer.lastName;
      } else {
        customerData.firstName = newCustomer.firstName;
        customerData.lastName = newCustomer.lastName;
        if (newCustomer.companyName)
          customerData.companyName = newCustomer.companyName;
      }

      console.log('Sending customer data:', customerData);
      console.log('Selected country:', selectedCountry);

      const createdCustomer = await apiService.createCustomer(customerData);

      // Add the new customer to the list
      setCustomers(prev => [...prev, createdCustomer]);

      // Show success message
      setSnackbar({
        open: true,
        message: t('customers.messages.createdSuccess'),
        severity: 'success',
      });

      handleCloseModal();
    } catch (err: any) {
      console.error('Error creating customer:', err);
      console.error('Error details:', err.response || err.message);

      // Show error message
      setSnackbar({
        open: true,
        message: err.message || t('customers.errors.failedToCreate'),
        severity: 'error',
      });
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingCustomer(null);
    setSelectedCountry('');
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingCustomer(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingCustomer) return;

    try {
      await apiService.deleteCustomer(deletingCustomer.id);

      // Remove the customer from the list
      setCustomers(prev => prev.filter(c => c.id !== deletingCustomer.id));

      // Show success message
      setSnackbar({
        open: true,
        message: t('customers.messages.deletedSuccess'),
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Error deleting customer:', err);

      // Show error message
      setSnackbar({
        open: true,
        message: err.message || t('customers.errors.failedToDelete'),
        severity: 'error',
      });
    } finally {
      // Always close modal
      handleCloseDeleteModal();
    }
  };

  const handleUpdateCustomer = async () => {
    if (!editingCustomer) return;

    // Determine customer type
    const selectedType = customerTypes.find(
      ct => ct.id === editingCustomer.customerType.id
    );
    const isBusiness = selectedType?.name.toLowerCase().includes('business');
    const isPrivate = selectedType?.name.toLowerCase().includes('private');

    // Validate required fields based on customer type
    const missingFields: string[] = [];
    if (!editingCustomer.email) missingFields.push('email');
    if (!editingCustomer.city) missingFields.push('city');
    if (!editingCustomer.phoneNumber) missingFields.push('phoneNumber');
    if (!editingCustomer.customerType.id) missingFields.push('customerType');
    if (!selectedCountry && !editingCustomer.country.id)
      missingFields.push('country');

    if (isBusiness && !editingCustomer.companyName) {
      missingFields.push('companyName');
    } else if (isPrivate) {
      if (!editingCustomer.firstName) missingFields.push('firstName');
      if (!editingCustomer.lastName) missingFields.push('lastName');
    }

    if (missingFields.length > 0) {
      setSnackbar({
        open: true,
        message: t('customers.messages.fillRequiredFields'),
        severity: 'error',
      });
      return;
    }

    try {
      // Prepare customer data based on type
      const customerData: any = {
        customerTypeId: editingCustomer.customerType.id,
        phoneNumber: editingCustomer.phoneNumber,
        city: editingCustomer.city,
        countryId: selectedCountry || editingCustomer.country.id,
        postCode: editingCustomer.postCode,
        address: editingCustomer.address,
        vatNumber: editingCustomer.vatNumber,
        evcNumber: editingCustomer.evcNumber,
        email: editingCustomer.email,
        autotunerEmail: editingCustomer.autotunerEmail || null,
        magicmotorsportsEmail: editingCustomer.magicmotorsportsEmail || null,
        slaveSerialNumber: editingCustomer.slaveSerialNumber || undefined,
        // password: 'TempPassword123!', // You might want to handle password separately
        isActive: editingCustomer.isActive,
        isVerified: editingCustomer.isVerified,
      };
      if (editingCustomer.customerGroupId) {
        customerData.customerGroupId = editingCustomer.customerGroupId;
      }

      // Add fields based on customer type
      if (isBusiness) {
        customerData.companyName = editingCustomer.companyName;
        if (editingCustomer.firstName)
          customerData.firstName = editingCustomer.firstName;
        if (editingCustomer.lastName)
          customerData.lastName = editingCustomer.lastName;
      } else {
        customerData.firstName = editingCustomer.firstName;
        customerData.lastName = editingCustomer.lastName;
        if (editingCustomer.companyName)
          customerData.companyName = editingCustomer.companyName;
      }

      console.log('Updating customer data:', customerData);

      const updatedCustomer = await apiService.updateCustomer(
        editingCustomer.id,
        customerData
      );

      // Update the customer in the list
      setCustomers(prev =>
        prev.map(c => (c.id === editingCustomer.id ? updatedCustomer : c))
      );

      // Show success message
      setSnackbar({
        open: true,
        message: t('customers.messages.updatedSuccess'),
        severity: 'success',
      });

      handleCloseEditModal();
    } catch (err: any) {
      console.error('Error updating customer:', err);
      console.error('Error details:', err.response || err.message);

      // Show error message
      setSnackbar({
        open: true,
        message: err.message || t('customers.errors.failedToUpdate'),
        severity: 'error',
      });
    }
  };

  const handleEditInputChange =
    // eslint-disable-next-line no-undef
    (field: keyof Customer) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (editingCustomer) {
        setEditingCustomer(prev =>
          prev ? { ...prev, [field]: event.target.value } : null
        );
      }
    };

  const handleEditSwitchChange =
    // eslint-disable-next-line no-undef
    (field: keyof Customer) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (editingCustomer) {
        setEditingCustomer(prev =>
          prev ? { ...prev, [field]: event.target.checked } : null
        );
      }
    };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleInputChange =
    (field: keyof typeof newCustomer) =>
    // eslint-disable-next-line no-undef
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setNewCustomer(prev => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleSwitchChange =
    (field: keyof typeof newCustomer) =>
    // eslint-disable-next-line no-undef
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setNewCustomer(prev => ({
        ...prev,
        [field]: event.target.checked,
      }));
    };

  const handleChangePage = (event: unknown, newPage: number) => {
    console.log(event);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    // eslint-disable-next-line no-undef
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('customers.title')}
      </Typography>

      {error && (
        <Alert variant="filled" severity="error" sx={{ mt: 2, mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mt: 3 }}>
        <CardContent>
          {/* Filters and Add Customer Controls */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'end',
              alignItems: 'center',
              mb: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
            }}
          >
            <Button
              variant={filtersOpen ? 'contained' : 'outlined'}
              startIcon={<FilterList />}
              onClick={() => setFiltersOpen(prev => !prev)}
              sx={{ minWidth: 'fit-content' }}
            >
              {t('customers.filters.button')}
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddCustomer}
              sx={{ minWidth: 'fit-content' }}
            >
              {t('customers.addCustomer')}
            </Button>
          </Box>

          <Collapse in={filtersOpen}>
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 2 }}>
                {t('customers.filters.title')}
              </Typography>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  flexWrap="wrap"
                  useFlexGap
                >
                  <TextField
                    size="small"
                    label={t('customers.filters.email')}
                    value={filterEmail}
                    onChange={e => setFilterEmail(e.target.value)}
                    sx={{ minWidth: 180 }}
                  />
                  <TextField
                    size="small"
                    label={t('customers.filters.phoneNumber')}
                    value={filterPhone}
                    onChange={e => setFilterPhone(e.target.value)}
                    sx={{ minWidth: 160 }}
                  />
                  <TextField
                    size="small"
                    label={t('customers.filters.firstName')}
                    value={filterFirstName}
                    onChange={e => setFilterFirstName(e.target.value)}
                    sx={{ minWidth: 140 }}
                  />
                  <TextField
                    size="small"
                    label={t('customers.filters.lastName')}
                    value={filterLastName}
                    onChange={e => setFilterLastName(e.target.value)}
                    sx={{ minWidth: 140 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>{t('customers.modals.add.customerGroup')}</InputLabel>
                    <Select
                      value={filterCustomerGroupId}
                      onChange={e => setFilterCustomerGroupId(e.target.value)}
                      label={t('customers.modals.add.customerGroup')}
                    >
                      <MenuItem value="">
                        <em>—</em>
                      </MenuItem>
                      {customerGroups.map(group => (
                        <MenuItem key={group.id} value={group.id}>
                          {group.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ mt: 2 }}>
                  <Button variant="contained" size="small" onClick={handleFiltersConfirm}>
                    {t('customers.filters.confirm')}
                  </Button>
                  <Button variant="outlined" size="small" onClick={handleFiltersReset}>
                    {t('customers.filters.reset')}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Collapse>

          {/* Customers Table */}
          <Box sx={{ width: '100%', overflowX: 'auto', minWidth: 0 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="h6">
                        {t('customers.table.companyName')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">
                        {t('customers.table.phoneNumber')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">
                        {t('customers.table.customerType')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">
                        {t('customers.table.city')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">
                        {t('customers.table.status')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h6">
                        {t('customers.table.createdAt')}
                      </Typography>
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && customers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          {t('customers.table.noData')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading &&
                    customers.map(customer => (
                    <TableRow key={customer.id}>
                      <TableCell>
                        {customer.companyName === '---'
                          ? `${customer.firstName} ${customer.lastName}`
                          : customer.companyName}
                      </TableCell>
                      <TableCell>{customer.phoneNumber}</TableCell>
                      <TableCell>
                        <Chip
                          label={customer.customerType.name}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{customer.city}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip
                            label={
                              customer.isActive
                                ? t('customers.status.active')
                                : t('customers.status.inactive')
                            }
                            size="small"
                            color={customer.isActive ? 'success' : 'default'}
                          />
                          {customer.isVerified && (
                            <Chip
                              label={t('customers.status.verified')}
                              size="small"
                              color="info"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(customer.createdAt)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            onClick={() => handleOpenAddCreditsModal(customer)}
                            size="small"
                            color="primary"
                          >
                            <IconCurrencyDollar />
                          </IconButton>
                          <IconButton
                            onClick={() => handleEdit(customer.id)}
                            size="small"
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(customer.id)}
                            size="small"
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>

      {/* Add Credits Modal */}
      <Dialog
        open={isAddCreditsModalOpen}
        onClose={handleCloseAddCreditsModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">
              {t('customers.modals.addCredits.title')}
            </Typography>
            <IconButton onClick={handleCloseAddCreditsModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {selectedCustomerForCredits && (
              <Typography variant="body2" color="text.secondary">
                {t('customers.modals.addCredits.customerLabel', {
                  name:
                    selectedCustomerForCredits.companyName &&
                    selectedCustomerForCredits.companyName !== '---'
                      ? selectedCustomerForCredits.companyName
                      : `${selectedCustomerForCredits.firstName ?? ''} ${selectedCustomerForCredits.lastName ?? ''}`.trim() ||
                        '—',
                })}
              </Typography>
            )}

            <FormControl fullWidth required>
              <InputLabel>{t('customers.modals.addCredits.type')}</InputLabel>
              <Select
                value={adjustType}
                onChange={e =>
                  setAdjustType(e.target.value as 'credit' | 'debit')
                }
                label={t('customers.modals.addCredits.type')}
              >
                <MenuItem value="credit">
                  {t('customers.modals.addCredits.typeCredit')}
                </MenuItem>
                <MenuItem value="debit">
                  {t('customers.modals.addCredits.typeDebit')}
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="number"
              label={t('customers.modals.addCredits.amount')}
              value={creditAmount}
              onChange={e => setCreditAmount(Number(e.target.value))}
              inputProps={{ min: 1 }}
            />

            <TextField
              fullWidth
              label={t('customers.modals.addCredits.reason')}
              value={adjustReason}
              onChange={e => setAdjustReason(e.target.value)}
              multiline
              minRows={2}
              placeholder={t('customers.modals.addCredits.reasonPlaceholder')}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseAddCreditsModal} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleAddCredits}
            variant="contained"
            color="primary"
          >
            {t('customers.modals.addCredits.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Customer Modal */}
      <Dialog
        open={isAddModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">
              {t('customers.modals.add.title')}
            </Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {/* Customer Type Selection */}
            <FormControl fullWidth required>
              <InputLabel>{t('customers.modals.add.customerType')}</InputLabel>
              <Select
                value={newCustomer.customerTypeId}
                onChange={e => {
                  const selectedTypeId = e.target.value;
                  const selectedType = customerTypes.find(
                    ct => ct.id === selectedTypeId
                  );
                  const isBusiness = selectedType?.name
                    .toLowerCase()
                    .includes('business');

                  setNewCustomer(prev => ({
                    ...prev,
                    customerTypeId: selectedTypeId,
                    // Clear fields when type changes
                    ...(isBusiness
                      ? { firstName: '', lastName: '' }
                      : { companyName: '' }),
                  }));
                }}
                label={t('customers.modals.add.customerType')}
              >
                {customerTypes.map(type => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Customer Group Selection */}
            <FormControl fullWidth>
              <InputLabel>{t('customers.modals.add.customerGroup')}</InputLabel>
              <Select
                value={newCustomer.customerGroupId}
                onChange={e =>
                  setNewCustomer(prev => ({
                    ...prev,
                    customerGroupId: e.target.value as string,
                  }))
                }
                label={t('customers.modals.add.customerGroup')}
              >
                <MenuItem value="">
                  <em>—</em>
                </MenuItem>
                {customerGroups.map(group => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Company Name - only for Business */}
            {(() => {
              const selectedType = customerTypes.find(
                ct => ct.id === newCustomer.customerTypeId
              );
              const isBusiness = selectedType?.name
                .toLowerCase()
                .includes('business');

              return (
                isBusiness && (
                  <TextField
                    fullWidth
                    label={t('customers.modals.add.companyName')}
                    value={newCustomer.companyName}
                    onChange={handleInputChange('companyName')}
                    required
                  />
                )
              );
            })()}

            {/* First Name + Last Name - only for Private */}
            {(() => {
              const selectedType = customerTypes.find(
                ct => ct.id === newCustomer.customerTypeId
              );
              const isPrivate = selectedType?.name
                .toLowerCase()
                .includes('private');

              return (
                isPrivate && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      label={t('customers.modals.add.firstName')}
                      value={newCustomer.firstName}
                      onChange={handleInputChange('firstName')}
                      required
                    />
                    <TextField
                      fullWidth
                      label={t('customers.modals.add.lastName')}
                      value={newCustomer.lastName}
                      onChange={handleInputChange('lastName')}
                      required
                    />
                  </Box>
                )
              );
            })()}

            {/* Email */}
            <TextField
              fullWidth
              label={t('customers.modals.add.email')}
              type="email"
              value={newCustomer.email}
              onChange={handleInputChange('email')}
              required
            />

            {/* Password */}
            <TextField
              fullWidth
              label={t('customers.modals.add.password')}
              type="password"
              value={newCustomer.password}
              onChange={handleInputChange('password')}
              required
            />

            {/* Country */}
            <FormControl fullWidth required>
              <InputLabel>{t('customers.modals.add.country')}</InputLabel>
              <Select
                value={selectedCountry}
                onChange={e => setSelectedCountry(e.target.value)}
                label={t('customers.modals.add.country')}
              >
                {countries.map(country => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name} ({country.isoCode}) {country.phoneCode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* City */}
            <TextField
              fullWidth
              label={t('customers.modals.add.city')}
              value={newCustomer.city}
              onChange={handleInputChange('city')}
              required
            />

            {/* Address */}
            <TextField
              fullWidth
              label={t('customers.modals.add.address')}
              value={newCustomer.address}
              onChange={handleInputChange('address')}
              multiline
              rows={2}
            />

            {/* Phone + Post Code */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label={t('customers.modals.add.phoneNumber')}
                value={newCustomer.phoneNumber}
                onChange={handleInputChange('phoneNumber')}
                required
              />
              <TextField
                fullWidth
                label={t('customers.modals.add.postCode')}
                value={newCustomer.postCode}
                onChange={handleInputChange('postCode')}
              />
            </Box>

            {/* VAT + EVC */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              {(() => {
                const selectedType = customerTypes.find(
                  ct => ct.id === newCustomer.customerTypeId
                );
                const isBusiness = selectedType?.name
                  .toLowerCase()
                  .includes('business');

                return (
                  isBusiness && (
                    <TextField
                      fullWidth
                      label={t('customers.modals.add.vatNumber')}
                      value={newCustomer.vatNumber}
                      onChange={handleInputChange('vatNumber')}
                    />
                  )
                );
              })()}
              <TextField
                fullWidth
                label={t('customers.modals.add.evcNumber')}
                value={newCustomer.evcNumber}
                onChange={handleInputChange('evcNumber')}
              />
            </Box>
            <TextField
              fullWidth
              label={t('customers.modals.add.slaveSerialNumber')}
              value={newCustomer.slaveSerialNumber}
              onChange={handleInputChange('slaveSerialNumber')}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Autotuner Email */}
              <TextField
                fullWidth
                label="Autotuner Email"
                type="email"
                value={newCustomer.autotunerEmail}
                onChange={handleInputChange('autotunerEmail')}
              />

              {/* Magic Motorsports Email */}
              <TextField
                fullWidth
                label="Magic Motorsports Email"
                type="email"
                value={newCustomer.magicmotorsportsEmail}
                onChange={handleInputChange('magicmotorsportsEmail')}
              />
            </Box>

            {/* Active + Verified */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newCustomer.isActive}
                    onChange={handleSwitchChange('isActive')}
                    color="primary"
                  />
                }
                label={t('customers.modals.add.active')}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={newCustomer.isVerified}
                    onChange={handleSwitchChange('isVerified')}
                    color="primary"
                  />
                }
                label={t('customers.modals.add.verified')}
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseModal} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveCustomer}
            variant="contained"
            color="primary"
          >
            {t('customers.modals.add.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Customer Modal */}
      {editingCustomer && (
        <Dialog
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">
                {t('customers.modals.edit.title')}
              </Typography>
              <IconButton onClick={handleCloseEditModal} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}
            >
              {/* Customer Type Selection - Disabled */}
              <FormControl fullWidth required>
                <InputLabel>
                  {t('customers.modals.add.customerType')}
                </InputLabel>
                <Select
                  value={editingCustomer.customerType.id}
                  disabled
                  label={t('customers.modals.add.customerType')}
                >
                  {customerTypes.map(type => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Customer Group Selection */}
              <FormControl fullWidth>
                <InputLabel>{t('customers.modals.add.customerGroup')}</InputLabel>
                <Select
                  value={editingCustomer.customerGroupId ?? ''}
                  onChange={e =>
                    setEditingCustomer(prev =>
                      prev
                        ? { ...prev, customerGroupId: e.target.value as string }
                        : null
                    )
                  }
                  label={t('customers.modals.add.customerGroup')}
                >
                  <MenuItem value="">
                    <em>—</em>
                  </MenuItem>
                  {customerGroups.map(group => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Company Name - only for Business */}
              {(() => {
                const selectedType = customerTypes.find(
                  ct => ct.id === editingCustomer.customerType.id
                );
                const isBusiness = selectedType?.name
                  .toLowerCase()
                  .includes('business');

                return (
                  isBusiness && (
                    <TextField
                      fullWidth
                      label={t('customers.modals.add.companyName')}
                      value={editingCustomer.companyName}
                      onChange={handleEditInputChange('companyName')}
                      required
                    />
                  )
                );
              })()}

              {/* First Name + Last Name - only for Private */}
              {(() => {
                const selectedType = customerTypes.find(
                  ct => ct.id === editingCustomer.customerType.id
                );
                const isPrivate = selectedType?.name
                  .toLowerCase()
                  .includes('private');

                return (
                  isPrivate && (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        fullWidth
                        label={t('customers.modals.add.firstName')}
                        value={editingCustomer.firstName}
                        onChange={handleEditInputChange('firstName')}
                        required
                      />
                      <TextField
                        fullWidth
                        label={t('customers.modals.add.lastName')}
                        value={editingCustomer.lastName}
                        onChange={handleEditInputChange('lastName')}
                        required
                      />
                    </Box>
                  )
                );
              })()}

              {/* Email */}
              <TextField
                fullWidth
                label={t('customers.modals.add.email')}
                type="email"
                value={editingCustomer.email}
                onChange={handleEditInputChange('email')}
                required
              />

              {/* Country */}
              <FormControl fullWidth required>
                <InputLabel>{t('customers.modals.add.country')}</InputLabel>
                <Select
                  value={selectedCountry}
                  onChange={e => setSelectedCountry(e.target.value)}
                  label={t('customers.modals.add.country')}
                >
                  {countries.map(country => (
                    <MenuItem key={country.id} value={country.id}>
                      {country.name} ({country.isoCode}) {country.phoneCode}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* City */}
              <TextField
                fullWidth
                label={t('customers.modals.add.city')}
                value={editingCustomer.city}
                onChange={handleEditInputChange('city')}
                required
              />

              {/* Address */}
              <TextField
                fullWidth
                label={t('customers.modals.add.address')}
                value={editingCustomer.address}
                onChange={handleEditInputChange('address')}
                multiline
                rows={2}
              />

              {/* Phone + Post Code */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label={t('customers.modals.add.phoneNumber')}
                  value={editingCustomer.phoneNumber}
                  onChange={handleEditInputChange('phoneNumber')}
                  required
                />
                <TextField
                  fullWidth
                  label={t('customers.modals.add.postCode')}
                  value={editingCustomer.postCode}
                  onChange={handleEditInputChange('postCode')}
                />
              </Box>

              {/* VAT + EVC */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                {(() => {
                  const selectedType = customerTypes.find(
                    ct => ct.id === editingCustomer.customerType.id
                  );
                  const isBusiness = selectedType?.name
                    .toLowerCase()
                    .includes('business');

                  return (
                    isBusiness && (
                      <TextField
                        fullWidth
                        label={t('customers.modals.add.vatNumber')}
                        value={editingCustomer.vatNumber}
                        onChange={handleEditInputChange('vatNumber')}
                      />
                    )
                  );
                })()}
                <TextField
                  fullWidth
                  label={t('customers.modals.add.evcNumber')}
                  value={editingCustomer.evcNumber}
                  onChange={handleEditInputChange('evcNumber')}
                />
              </Box>
              <TextField
                fullWidth
                label={t('customers.modals.add.slaveSerialNumber')}
                value={editingCustomer.slaveSerialNumber || ''}
                onChange={handleEditInputChange('slaveSerialNumber')}
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Autotuner Email */}
                <TextField
                  fullWidth
                  label="Autotuner Email"
                  type="email"
                  value={editingCustomer.autotunerEmail || ''}
                  onChange={handleEditInputChange('autotunerEmail')}
                />

                {/* Magic Motorsports Email */}
                <TextField
                  fullWidth
                  label="Magic Motorsports Email"
                  type="email"
                  value={editingCustomer.magicmotorsportsEmail || ''}
                  onChange={handleEditInputChange('magicmotorsportsEmail')}
                />
              </Box>

              {/* Credits (Balance) - Disabled */}
              <TextField
                fullWidth
                label={t('customers.modals.add.credits')}
                value={editingCustomer.balance ?? 0}
                disabled
                type="number"
              />

              {/* Active + Verified */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingCustomer.isActive}
                      onChange={handleEditSwitchChange('isActive')}
                      color="primary"
                    />
                  }
                  label={t('customers.modals.add.active')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={editingCustomer.isVerified}
                      onChange={handleEditSwitchChange('isVerified')}
                      color="primary"
                    />
                  }
                  label={t('customers.modals.add.verified')}
                />
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseEditModal} color="secondary">
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleUpdateCustomer}
              variant="contained"
              color="primary"
            >
              {t('customers.modals.edit.update')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Modal */}
      {deletingCustomer && (
        <Dialog
          open={isDeleteModalOpen}
          onClose={handleCloseDeleteModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">
                {t('customers.modals.delete.title')}
              </Typography>
              <IconButton onClick={handleCloseDeleteModal} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent>
            <Typography>
              {t('customers.modals.delete.confirm')}{' '}
              <strong>{deletingCustomer.companyName}</strong>?{' '}
              {t('customers.modals.delete.warning')}
            </Typography>
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDeleteModal} color="secondary">
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
            >
              {t('customers.modals.delete.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          variant="filled"
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomersPage;
